import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Card, Modal } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import {
  Folder,
  Requirement,
  Status,
} from "../../redux/interfaces/folder.types.ts";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "../../redux/hooks.ts";
import {
  setActiveRequirement as setActiveRequirementVault,
  setActiveVault,
} from "../../redux/slices/vaultSlice.ts";
import {
  clearActiveRequirement,
  setActiveRequirement,
} from "../../redux/slices/requirementsSlice.ts";
import {
  documentationMap,
  DocumentationMapOptions,
} from "../vault/documentationMap.tsx";
import { getSubfoldersMapByIdToSlug } from "../../utils/subfolders/subfoldersMapByIdToSlug.ts";
import { validToUpload } from "../vault/fixtures/validToUpload.ts";
import { canEditValidRequirement } from "../../utils/auth/requirementEditPermissions.ts";
import { BdcCompanyDataStep } from "../vault/forms/BdcCompanyDataStep.tsx";
import { isBdcAuthorizationRequirement } from "../vault/utils/requirementGrouping.ts";

interface Props {
  folder: Folder;
  disabled?: boolean;
}

export const FormView: React.FC<Props> = ({ folder, disabled = false }) => {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [hasSubmitAction, setHasSubmitAction] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitFnRef = useRef<(() => Promise<boolean>) | null>(null);

  const { activeRequirement } = useAppSelector((state) => state.requirements);
  const { activeFolder } = useAppSelector((state) => state.folder);
  // Null-safe: some test stores (and any pre-auth render) omit the auth slice,
  // so reading state.auth and destructuring would crash.
  const userInfo = useAppSelector((state) => state.auth?.userInfo);
  const canEditValidInPlace = canEditValidRequirement(userInfo?.groups);

  const subfolderSlugMap = getSubfoldersMapByIdToSlug(activeFolder);

  const resetModalSubmit = useCallback(() => {
    submitFnRef.current = null;
    setHasSubmitAction(false);
    setIsSubmitting(false);
  }, []);

  const handleRegisterSubmit = useCallback(
    (submitFn: () => Promise<boolean>) => {
      submitFnRef.current = submitFn;
      setHasSubmitAction(true);
    },
    [],
  );

  const handleRegisterGeneralSubmit = useCallback(
    (submitFn: (() => Promise<boolean>) | null) => {
      submitFnRef.current = submitFn;
      setHasSubmitAction(Boolean(submitFn));
    },
    [],
  );

  const documentationMapOptions: DocumentationMapOptions = useMemo(
    () => ({
      onRegisterSubmit: handleRegisterSubmit,
      onRegisterProofOfAddressSubmit: handleRegisterSubmit,
      onRegisterGeneralSubmit: handleRegisterGeneralSubmit,
      // Staff views in /folders/:id must be able to edit OCR-verified fields
      // (e.g. to correct extraction mistakes after Deshacer envío a revisión).
      isAdminContext: true,
    }),
    [handleRegisterGeneralSubmit, handleRegisterSubmit],
  );

  const formElement = documentationMap(
    activeRequirement!.slug,
    subfolderSlugMap[activeRequirement!.subfolder],
    folder,
    documentationMapOptions,
  );
  const activeSubfolderSlug =
    subfolderSlugMap[activeRequirement!.subfolder] ?? "";
  const showBdcCompanyData =
    isBdcAuthorizationRequirement(
      activeRequirement!.slug,
      activeRequirement!.type,
    ) && activeSubfolderSlug.endsWith("-pm");

  const syncActiveRequirement = useCallback(
    (requirement: Requirement | null | undefined) => {
      if (!requirement) return;
      dispatch(setActiveRequirementVault(requirement));
      dispatch(setActiveRequirement(requirement));
    },
    [dispatch],
  );

  const getLatestActiveRequirement = useCallback(
    () => store.getState().requirements.activeRequirement,
    [store],
  );

  const onInitActiveVaultRequirement = useCallback(
    (
      requirement:
        | Requirement
        | null
        | undefined = getLatestActiveRequirement(),
    ) => {
      syncActiveRequirement(requirement);
    },
    [getLatestActiveRequirement, syncActiveRequirement],
  );

  useEffect(() => {
    dispatch(setActiveVault(folder));
  }, [dispatch, folder]);

  useEffect(() => {
    onInitActiveVaultRequirement(activeRequirement);
  }, [activeRequirement, onInitActiveVaultRequirement]);

  useEffect(() => {
    resetModalSubmit();
  }, [activeRequirement?.id, resetModalSubmit]);

  const onClosedModal = () => {
    const latestRequirement = getLatestActiveRequirement();
    setIsModalVisible(false);
    resetModalSubmit();
    dispatch(clearActiveRequirement());
    syncActiveRequirement(latestRequirement);
  };

  const onOpenModal = () => {
    resetModalSubmit();
    setIsModalVisible(true);
    onInitActiveVaultRequirement();
  };

  const canSubmitRequirement =
    hasSubmitAction &&
    Boolean(
      activeRequirement?.status &&
        validToUpload.includes(activeRequirement.status),
    );

  // Admin in-place edit: a valid requirement can be edited and saved WITHOUT
  // changing its status. GeneralForm's registered submit handler omits
  // `status` in this path, so the button only needs a distinct label.
  const canSaveInPlace =
    hasSubmitAction &&
    canEditValidInPlace &&
    activeRequirement?.status === Status.Valid;

  const onSubmitRequirement = async () => {
    if (!submitFnRef.current || isSubmitting || disabled) return;

    setIsSubmitting(true);
    try {
      const wasSubmitted = await submitFnRef.current();
      if (wasSubmitted) {
        onClosedModal();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Modal
        title={activeRequirement?.name}
        open={isModalVisible}
        onCancel={onClosedModal}
        width="75vw"
        centered
        destroyOnClose
        style={{ padding: 0, maxHeight: "80vh", overflow: "scroll" }}
        footer={[
          <Button key="close" onClick={onClosedModal} disabled={isSubmitting}>
            Cerrar
          </Button>,
          canSubmitRequirement ? (
            <Button
              key="submit"
              type="primary"
              loading={isSubmitting}
              disabled={disabled}
              onClick={onSubmitRequirement}
            >
              Enviar a revisión
            </Button>
          ) : canSaveInPlace ? (
            <Button
              key="save-in-place"
              type="primary"
              loading={isSubmitting}
              disabled={disabled}
              onClick={onSubmitRequirement}
            >
              Guardar cambios
            </Button>
          ) : null,
        ]}
      >
        <Card
          className="w-full h-full"
          style={{ padding: "16px", maxHeight: "100%", overflow: "scroll" }}
        >
          {/* Stable key prevents the child form from being unmounted when
              Redux updates activeRequirement (e.g. after Deshacer a revisión),
              which would otherwise re-run the submit registration effect. */}
          <div key={activeRequirement?.id}>{formElement?.component}</div>
          {showBdcCompanyData ? (
            <div className="mt-6" data-testid="bdc-company-data-admin">
              <BdcCompanyDataStep
                header={<span>Datos empresariales usados para Buró de Crédito</span>}
                isAdminContext
              />
            </div>
          ) : null}
        </Card>
      </Modal>
      <Button
        type="dashed"
        onClick={onOpenModal}
        disabled={disabled}
        icon={<EyeOutlined />}
        className="my-4"
      >
        Ver preguntas
      </Button>
    </div>
  );
};
